import React, { useMemo } from 'react';
import { curveMonotoneX } from '@visx/curve';
import { ComposedChart } from '../../charts/composed-chart';
import { Area } from '../../charts/area';
import { Line } from '../../charts/line';
import { Grid } from '../../charts/grid';
import { XAxis } from '../../charts/x-axis';
import { YAxis } from '../../charts/y-axis';
import { createPortal } from 'react-dom';
import { ChartTooltip } from '../../charts/tooltip';
import { useChartHover, useChartStable } from '../../charts/chart-context';
import { getFormatter } from '../formatters';
import {
  useIsDarkMode,
  useChartPaletteId,
  compactFormat,
  seriesColor,
  SYNTH_X_KEY,
  toBklitRows,
  seriesHasData,
  ChartSkeletonLayer,
  isNumeric,
  pointMarkers,
  labelColor,
  RnPointLabels,
} from './chartTheme';
import { isInkShell } from '../../../styles/chartColors';
import { ChartLegend } from './ChartLegend';
import { prefersReducedMotion } from '../AnimatedValue';
import '../ReportsNative.css';

/**
 * Sparkline tooltip rendered in a body-level portal directly under the chart's
 * container. The kit's own box is sized and pinned for full-height charts, so
 * inside a ~38px sparkline it lands on top of the line it describes.
 */
function SparkTooltipPortal({ title, rows }) {
  const { tooltipData } = useChartHover();
  const { containerRef } = useChartStable();
  if (!tooltipData || !containerRef?.current || typeof document === 'undefined') return null;
  const rect = containerRef.current.getBoundingClientRect();
  const heading = typeof title === 'function' ? title(tooltipData.point, tooltipData.index) : title;
  const lines = rows(tooltipData.point, tooltipData.index);
  return createPortal(
    <div className="rn-spark-tip" style={{ left: rect.left, top: rect.bottom + 8 }} role="tooltip">
      {heading && <div className="rn-spark-tip-title">{heading}</div>}
      {lines.map((l) => (
        <div key={l.label} className="rn-spark-tip-row">
          <span className="rn-spark-tip-dot" style={{ background: l.color }} />
          <span>{l.label}</span>
          <span className="rn-mono">{l.value}</span>
        </div>
      ))}
    </div>,
    document.body
  );
}
SparkTooltipPortal.displayName = 'ChartTooltipOverlay';

/**
 * Orange gradient area trend (per financial-pnl.html trend cards and
 * operations-labor.html Room Revenue Trend). First series renders as the
 * gradient area; additional series render as lines (e.g. occupied room
 * nights overlay). Rendered with the vendored bklit kit
 * (src/components/charts) — categorical x values get synthetic dates per
 * the LODGEIC.md recipe.
 *
 * Props:
 *   data       [{ [xKey], [series keys]... }]
 *   xKey       x-axis data key
 *   series     [{ key, label, color?, type?: 'area'|'line', format?, axis?: 'right' }]
 *              — first defaults to 'area', rest to 'line'. `format`
 *              overrides yFormat for that series' tooltip value; `axis:
 *              'right'` plots it on a secondary right-hand axis (for
 *              overlays in different units, e.g. room nights vs revenue)
 *   labels     show mono point labels above the first series (default false)
 *   yFormat    format key or fn for ticks/tooltip/labels (default 'currencyCompact')
 *   currency   ISO code for currency formats
 *   height     px (default 240)
 *   dots       show white-filled points (default false — the line/area reads
 *              cleaner without them, and point count varies with the slice)
 *   legend     show compact legend when >1 series (default true)
 *   minimal    KPI-card sparkline mode: no grid/axes/ticks/tooltip, just the
 *              gradient area (default false)
 *   tooltip    keep the hover tooltip in minimal mode (default false). Values
 *              shown are the real ones — the sparkline rebases the drawn
 *              series on its minimum, but the tooltip reads the source row.
 *   tooltipTitle (point, index) => string header for the minimal tooltip
 *              (default: the x label)
 */
export function TrendAreaChart({
  data = [],
  xKey,
  series = [],
  labels = false,
  yFormat = 'currencyCompact',
  currency,
  height = 240,
  dots = false,
  legend = true,
  // 'peak' (default) labels only the top of the scale — the high-water
  // mark. 'full' restores the tick ladder for charts read for values
  // rather than shape.
  yAxis = 'peak',
  minimal = false,
  tooltip = false,
  tooltipTitle,
  yDomain,
  margin,
  loading = false,
}) {
  const dark = useIsDarkMode();
  useChartPaletteId(); // repaint on a palette switch (series colours read the palette live)
  const currencyOpts = currency ? { currency } : undefined;
  const fmt = getFormatter(yFormat, currencyOpts);
  const tickFmt = getFormatter(compactFormat(yFormat), currencyOpts);
  const rightSeries = series.filter((s) => s.axis === 'right');
  const rightTickFmt = getFormatter(compactFormat(rightSeries[0]?.format || yFormat), currencyOpts);
  const fmtByKey = new Map(series.map((s) => [s.key, getFormatter(s.format || yFormat, currencyOpts)]));

  const rows = useMemo(() => {
    const keys = series.map((s) => s.key);
    const built = toBklitRows(data, keys);
    if (minimal && built.length > 0) {
      // recharts ['dataMin','dataMax'] parity: the kit anchors positive
      // domains at 0, which flattens a 44px sparkline — rebase on the
      // series minimum so the line uses the full height. No axes/tooltip
      // in minimal mode, so the shift is invisible.
      let min = Infinity;
      for (const r of built) {
        for (const k of keys) {
          if (isNumeric(r[k]) && r[k] < min) min = r[k];
        }
      }
      const floor = yDomain && isNumeric(yDomain[0]) ? yDomain[0] : min;
      if (Number.isFinite(floor) && floor > 0) {
        for (const r of built) {
          for (const k of keys) {
            if (isNumeric(r[k])) r[k] -= floor;
          }
        }
      }
    }
    return built;
  }, [data, series, minimal, yDomain]);
  const xLabels = useMemo(() => rows.map((r) => String(r.__rnSrc?.[xKey] ?? '')), [rows, xKey]);

  // Loading with nothing to draw yet. The chart stays mounted and only the
  // skeleton LAYER swaps out when the payload lands, so the data arrives as
  // the series drawing itself in rather than one tree replacing another.
  const skeleton = loading && rows.length === 0;

  // Markers only on sparse series — dense daily data reads as noise and the
  // kit decimates the stroke, so per-point dots can sit off the path.
  const sparse = rows.length <= 14;

  const base = margin || { top: labels ? 22 : 10, right: 16, bottom: 0, left: 0 };
  const kitMargin = minimal
    ? base
    : {
        top: base.top,
        right: base.right + (rightSeries.length > 0 ? 44 : 0),
        bottom: base.bottom + (isInkShell() ? 28 : 34),
        left: base.left + (isInkShell() ? 46 : 58),
      };

  return (
    <div>
      {legend && <ChartLegend series={series} />}
      <div className="rn-chart-body" style={{ height }}>
        <ComposedChart
          className="rn-bklit"
          data={rows}
          xDataKey={SYNTH_X_KEY}
          xLabels={xLabels}
          margin={kitMargin}
          aspectRatio={null}
          style={{ height: '100%' }}
          animationDuration={prefersReducedMotion() ? 0 : undefined}
        >
          {!minimal && <Grid horizontal numTicksRows={4} />}
          {/* The x-axis stays mounted — with no data it draws no ticks, and
              remounting it strands its label portals as doubled-up ticks. The
              y-axis is held back: it would label the empty-domain fallback.
              Both gutters are margin-reserved, so nothing shifts either way. */}
          {!minimal && <XAxis />}
          {!minimal && !skeleton && <YAxis peak={yAxis === 'peak'} numTicks={4} formatValue={(v) => tickFmt(v)} />}
          {!minimal && rightSeries.length > 0 && (
            <YAxis yAxisId="right" orientation="right" peak={yAxis === 'peak'} numTicks={4} formatValue={(v) => rightTickFmt(v)} />
          )}
          {minimal && tooltip && !skeleton && (
            <SparkTooltipPortal
              title={tooltipTitle || ((point, i) => xLabels[i] ?? '')}
              rows={(point) =>
                series.map((s, i) => ({
                  color: seriesColor(s, i, dark),
                  label: s.label || s.key,
                  value: (fmtByKey.get(s.key) || fmt)(point.__rnSrc?.[s.key]),
                }))
              }
            />
          )}
          {(!minimal || tooltip) && !skeleton && (
            <ChartTooltip
              showDatePill={!minimal}
              // In sparkline mode the kit box is hidden; SparkTooltipPortal
              // above draws the panel under the tile. The crosshair dot stays.
              panelStyle={minimal ? { display: 'none' } : undefined}
              title={minimal ? tooltipTitle || ((point, i) => xLabels[i] ?? '') : null}
              rows={(point) =>
                series.map((s, i) => ({
                  color: seriesColor(s, i, dark),
                  label: s.label || s.key,
                  value: (fmtByKey.get(s.key) || fmt)(point.__rnSrc?.[s.key]),
                }))
              }
            />
          )}
          {skeleton && (
            <ChartSkeletonLayer
              color={seriesColor(series[0] || {}, 0, dark)}
              curve={curveMonotoneX}
              withArea
            />
          )}
          {series.map((s, i) => {
            if (!seriesHasData(rows, s.key)) return null;
            const color = seriesColor(s, i, dark);
            const type = s.type || (i === 0 ? 'area' : 'line');
            const yAxisId = s.axis === 'right' ? 'right' : undefined;
            if (type === 'area') {
              return (
                <Area
                  key={s.key}
                  dataKey={s.key}
                  yAxisId={yAxisId}
                  curve={curveMonotoneX}
                  fill={color}
                  fillOpacity={0.22}
                  gradientToOpacity={0}
                  stroke={color}
                  strokeWidth={2.5}
                  showMarkers={dots && !minimal && sparse}
                  markers={pointMarkers(color, 3)}
                />
              );
            }
            return (
              <Line
                key={s.key}
                dataKey={s.key}
                yAxisId={yAxisId}
                curve={curveMonotoneX}
                stroke={color}
                strokeWidth={2}
                showMarkers={dots && !minimal && sparse}
                markers={pointMarkers(color, 2.6)}
              />
            );
          })}
          {labels && !minimal && sparse && series.length > 0 && seriesHasData(rows, series[0].key) && (
            <RnPointLabels
              k={series[0].key}
              formatter={(v) => (fmtByKey.get(series[0].key) || fmt)(v)}
              fill={labelColor(dark)}
              fontSize={11}
            />
          )}
        </ComposedChart>
      </div>
    </div>
  );
}

export default TrendAreaChart;
